import React, { useMemo, useState } from 'react';

import { Button } from '~/shadcn/components/ui/Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/shadcn/components/ui/Dialog';
import { Label } from '~/shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shadcn/components/ui/Select';
import dayjs from '~/utils/dayjsConfig';

function ItemsFromWeekSelector({ isOpen, onOpenChange, onSubmit }) {
    const currentYear = dayjs().isoWeekYear();
    const initialWeek = useMemo(() => String(dayjs().isoWeek()), []);
    const initialYear = useMemo(() => String(dayjs().isoWeekYear()), []);
    const [week, setWeek] = useState(() => initialWeek);
    const [year, setYear] = useState(() => initialYear);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const weekOptions = useMemo(() => {
        // Equivalent to original Moment.js logic: moment().isoWeekYear(Number(year)).isoWeeksInYear()
        // Day.js doesn't support chaining isoWeekYear() setter the same way, so we use January 4th
        // which is always in week 1 of the ISO year, avoiding edge cases where Jan 1 falls on
        // Fri/Sat/Sun (which belong to previous year's ISO week)
        const isoWeeks = dayjs(`${year}-01-04`).isoWeeksInYear();
        return Array.from({ length: isoWeeks }, (_, i) => String(i + 1));
    }, [year]);
    const yearOptions = useMemo(() => [String(currentYear), String(currentYear - 1)], [currentYear]);

    const handleSubmit = async () => {
        if (!week || !year) return;
        setIsSubmitting(true);
        try {
            if (typeof onSubmit === 'function') {
                await onSubmit({ week: Number(week), year: Number(year) });
            }

            if (typeof onOpenChange === 'function') {
                onOpenChange(false);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[420px]">
                <DialogHeader>
                    <DialogTitle>Select Week and Year for Item Availability</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <h3>
                        Current Week: {initialWeek} {initialYear}
                    </h3>
                    <div className="flex items-center gap-3">
                        <Label htmlFor="week-select" className="w-20">
                            Week
                        </Label>
                        <Select value={week} onValueChange={setWeek}>
                            <SelectTrigger id="week-select" className="w-[160px]">
                                <SelectValue placeholder="Select week" />
                            </SelectTrigger>
                            <SelectContent>
                                {weekOptions.map((w) => (
                                    <SelectItem key={w} value={w}>
                                        {w}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center gap-3">
                        <Label htmlFor="year-select" className="w-20">
                            Year
                        </Label>
                        <Select value={year} onValueChange={setYear}>
                            <SelectTrigger id="year-select" className="w-[160px]">
                                <SelectValue placeholder="Select year" />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions.map((y) => (
                                    <SelectItem key={y} value={y}>
                                        {y}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={!week || !year || isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { ItemsFromWeekSelector };
